import React, { useEffect, Suspense } from 'react';
import { useDetectorDesborde } from './hooks/useDetectorDesborde';
import { useDetectorAtrapado } from './hooks/useDetectorAtrapado';
import { useIdioma } from './i18n/idioma';
import { tituloDeVista } from './i18n/titulosDeVista';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DialogProvider } from './contexts/DialogContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { AIChatProvider } from './contexts/AIChatContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { HelpVideoProvider } from './contexts/HelpVideoContext';

import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import DataProviders from './components/DataProviders';
import Sidebar from './components/Sidebar';
import AIChatPanel from './components/ai/AIChatPanel';
import VoiceConversationModal from './components/voice/VoiceConversationModal';
import HelpVideoBox from './components/help/HelpVideoBox';

import OfflineBadge from './components/ui/OfflineBadge';
import { initOfflineSync } from './services/offlineSync';
import { useCierreDeTurnosVencidos } from './hooks/useCierreDeTurnosVencidos';
import { vigilarLecturas } from './services/vigilarLecturas';
import WorkflowController from './components/WorkflowController';
import { useNewBuildDetector } from './hooks/useNewBuildDetector';
import { useBloqueoAutomaticoDeScroll, useCerrarConEscape } from './hooks/useBodyScrollLock';
import { useVolverArribaAlNavegar } from './hooks/useVolverArriba';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { canUseAI } from './constants/roles';
import { esLanding, esRutaPublica } from './utils/rutas';

import LoginView from './components/LoginView';
import LandingPage from './components/LandingPage';
// Banco de pruebas visual — solo se alcanza en desarrollo (ver ruta /design abajo).
import DesignLabView from './views/DesignLabView';
import RazaLoader from './components/ui/RazaLoader';
import { ShieldX } from 'lucide-react';
import ClientPortalLayout from './components/ClientPortalLayout';
import NotificationsBell from './components/NotificationsBell';

// --- LAZY LOADED VIEWS ---
const DashboardView = lazyWithRetry(() => import('./views/DashboardView'));
const ClientsView = lazyWithRetry(() => import('./views/ClientsView'));
const EstimatesView = lazyWithRetry(() => import('./views/EstimatesView'));
const ProjectsView = lazyWithRetry(() => import('./views/ProjectsView'));
const FinanceView = lazyWithRetry(() => import('./views/FinanceView'));

const FollowUpsView = lazyWithRetry(() => import('./views/FollowUpsView'));

const CalendarView = lazyWithRetry(() => import('./views/CalendarView'));
const PricebookView = lazyWithRetry(() => import('./views/PricebookView'));

const ProposalView = lazyWithRetry(() => import('./views/ProposalView'));
const PublicProposalView = lazyWithRetry(() => import('./views/PublicProposalView'));

const SettingsView = lazyWithRetry(() => import('./views/SettingsView'));

// DesignShowcaseView is intentionally not routed — the design portfolio it
// renders has no data source, so the page could only ever say "No Design
// Available". The file is kept for whenever the feature gets built for real.
const SuperAdminView = lazyWithRetry(() => import('./views/SuperAdminView'));
const AIChatView = lazyWithRetry(() => import('./views/AIChatView'));
const WorkforceView = lazyWithRetry(() => import('./components/workforce/WorkforceView'));
const ServicesView = lazyWithRetry(() => import('./views/ServicesView'));
const InventoryView = lazyWithRetry(() => import('./views/EquipmentView'));
const ClassesView = lazyWithRetry(() => import('./views/ClassesView'));
const SetupWizard = lazyWithRetry(() => import('./components/SetupWizard'));
const NotFoundView = lazyWithRetry(() => import('./views/NotFoundView'));

/**
 * Fallback de ruta — Sistema 2.0: esqueleto con la silueta de una vista
 * (encabezado + banda de métricas + filas) en vez de una ruleta centrada.
 * La página se siente instantánea porque el layout ya está ahí.
 */
const LoadingFallback = () => (
    <div className="w-full space-y-6 pt-2" aria-busy="true" aria-label="Cargando">
        <div className="flex items-end justify-between gap-4">
            <div className="space-y-2.5">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton h-6 w-52" />
            </div>
            <div className="skeleton h-10 w-32 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-acero-200 rounded-2xl overflow-hidden border border-acero-200">
            {[0, 1, 2].map(i => (
                <div key={i} className="bg-white p-6 space-y-3">
                    <div className="skeleton h-3 w-24" />
                    <div className="skeleton h-8 w-32" />
                </div>
            ))}
        </div>
        <div className="bg-white rounded-2xl border border-acero-200 p-5 space-y-3">
            {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="flex items-center gap-3">
                    <div className="skeleton h-7 w-7 rounded-lg shrink-0" />
                    <div className="skeleton h-3.5 flex-1" />
                    <div className="skeleton h-3.5 w-20 shrink-0" />
                </div>
            ))}
        </div>
    </div>
);

/* Aquí vivía una segunda clase `ErrorBoundary`, escrita a mano y con el mismo
 * nombre que la de `components/ErrorBoundary.tsx`. Al envolver desde DENTRO,
 * cazaba siempre antes que la buena, con dos consecuencias que llegaban a
 * producción —comprobado: su texto viajaba en el bundle compilado—:
 *
 *   · El cliente veía un `<h1>` rojo con el mensaje técnico del error en vez de
 *     la tarjeta con botón de recargar.
 *   · Y sobre todo: su `componentDidCatch` solo hacía `console.error`, así que
 *     NUNCA llamaba a `reportFailure`. Todo lo que reventara dentro de la app
 *     —o sea, la app entera— era invisible para el registro de errores.
 *
 * Ahora se usa la de `components/`, que es la que reporta. Ver la prueba
 * `App.errorBoundary.test.ts`, que falla si vuelve a aparecer una segunda. */

const AppContent: React.FC = () => {
    const { t } = useIdioma();
    const { settingsLoading, settings, loadFailed, retryLoad } = useSettings();
    const { activeWorkspace, status: workspaceStatus, retry: retryWorkspace } = useWorkspace();
    const { currentView, navigateTo } = useNavigation();
    const { isAuthorized, loading: authLoading, currentUser, userRole, clientId, logout, retryAuth, authError, isSuperAdmin, authStalled } = useAuth();

    // Ctrl/⌘+Shift+O marca lo que se sale de su recuadro. Va aquí, sobre todas
    // las vistas, porque el desborde depende del ancho y de los datos reales.
    useDetectorDesborde();

    // Ctrl/⌘+Shift+S marca el contenido que nadie puede traer a la vista: un
    // scroll que nunca llega a activarse y un padre que recorta lo que sobra.
    useDetectorAtrapado();

    // Offline sync
    useEffect(() => {
        initOfflineSync();
    }, []);

    // Detect new deploys while a tab is open and prompt the user to reload.
    // Without this, users who keep tabs open across deploys keep running the
    // old bundle indefinitely — they show up in error logs as "stale build_id"
    // entries even though the bug they're hitting was already fixed.
    useNewBuildDetector();

    /* Mientras haya un modal abierto, la pagina de debajo no se mueve. Va aqui
       y no en cada modal: son 39 los que montan una cortina a pantalla completa
       y solo cuatro lo pedian. Ver hooks/useBodyScrollLock.ts. */
    useBloqueoAutomaticoDeScroll();

    /* Escape cierra la ventana de encima. Se comprobo pulsandolo en los modales
       reales y ninguno cerraba. Ver hooks/useBodyScrollLock.ts. */
    useCerrarConEscape();

    /* Cambiar de vista empieza por arriba. React Router no toca el scroll, y
       como la que se mueve es la página (no una caja interna), al saltar de una
       lista larga a otra pantalla el navegador te deja a media altura de algo
       que acabas de abrir. Ver hooks/useVolverArriba.ts. */
    useVolverArribaAlNavegar();

    /* Cerrar los turnos que se quedaron abiertos, una vez por taller.
       RLS acota el UPDATE a los talleres de la persona — se puede llamar desde
       el cliente sin riesgo.

       El id del taller entra EXPLÍCITO. Antes se llamaba en cuanto la sesión
       quedaba autorizada, o sea antes de que `loadWorkspaces()` hubiera vuelto
       de la red: `requireWorkspaceId()` lanzaba y, como las dependencias del
       efecto no volvían a cambiar, no se reintentaba en toda la sesión. Ocho
       personas se quedaron sin poder fichar por eso el 22-ago-2026. El porqué
       de no fiarse de `workspaceStatus === 'resolved'` está en el hook. */
    useCierreDeTurnosVencidos({ isAuthorized, isSuperAdmin, wsId: activeWorkspace.id });

    // Pre-warm the TTS phrase cache so the first voice reply doesn't pay the
    // cold-start fetch cost. Fire-and-forget; failures are non-fatal (the
    // phrases load on demand instead).
    //
    // Gated on canUseAI, not merely on isAuthorized. The pack is 43 WAV files
    // / ~4.3 MB, and only Admin and Office can ever open the assistant that
    // plays them — the panel isn't mounted for anyone else (see :497) and the
    // route redirects (:484). Before this gate, a clock-only Laborer on phone
    // data downloaded the whole pack at every boot for a feature they cannot
    // reach, then held it in IndexedDB competing for the same quota the
    // cache's own self-disable logic exists to survive.
    //
    // Skip entirely on devices that lack the underlying APIs (in-app webviews
    // like Google Search App, Instagram, Facebook) — otherwise the pre-warm
    // floods the error log with "undefined is not a constructor" and
    // "Can't find variable: indexedDB" warnings.
    //
    // The AudioWorklet pre-warm that used to live here was removed: it
    // compiled public/tts-stream-worklet.js for `streamAndPlay`
    // (services/tts/router.ts:315), which has no callers, so the work was
    // never used by anything.
    useEffect(() => {
        if (!isAuthorized || !canUseAI(userRole)) return;
        let cancelled = false;
        (async () => {
            const cap = await import('./utils/voiceSupported');
            if (cancelled) return;
            if (cap.ttsCacheSupported()) {
                import('./services/tts/cache').then((m) => {
                    m.prepopulateFromManifest().catch((err) => {
                        console.info('[App] phrase pre-cache skipped:', err?.message || err);
                    });
                }).catch(() => { /* lazy import failed; non-fatal */ });
            }
        })().catch((err) => {
            // Belt-and-suspenders: any capability-probe failure degrades to
            // info instead of escaping to unhandledrejection.
            console.info('[App] pre-warm probe failed', err);
        });
        return () => { cancelled = true; };
    }, [isAuthorized, userRole]);

    // Mobile sidebar toggle (must be before any conditional returns per React hooks rules)
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);

    // Desktop sidebar collapse — persisted so the preference survives reloads
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => {
        try { return localStorage.getItem('raza-sidebar-collapsed') === '1'; } catch { return false; }
    });
    const toggleSidebarCollapsed = React.useCallback(() => {
        setIsSidebarCollapsed(prev => {
            try { localStorage.setItem('raza-sidebar-collapsed', prev ? '0' : '1'); } catch { /* ignore */ }
            return !prev;
        });
    }, []);

    // ⌘B / Ctrl+B alterna la barra lateral — el atajo universal para esto
    // (VS Code, Notion, Linear). Se ignora mientras se escribe en un campo o en
    // un editable, para no robarle el "negrita" a un editor de texto.
    React.useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'b' && e.key !== 'B') return;
            if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
            const el = e.target as HTMLElement | null;
            if (el) {
                const tag = el.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return;
            }
            e.preventDefault();
            toggleSidebarCollapsed();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [toggleSidebarCollapsed]);

    // Hooks MUST be called in the same order on every render — no conditional returns above this line.
    const location = useLocation();
    const isPublicRoute = esRutaPublica(location.pathname);

    // --- DESIGN LAB (solo desarrollo) ---
    // Banco de pruebas visual del sistema de diseño. Se monta antes de cualquier
    // comprobación de sesión para poder revisar los componentes sin pasar por el
    // login, y `import.meta.env.DEV` lo deja fuera del bundle de producción.
    if (import.meta.env.DEV && location.pathname === '/design') {
        return <DesignLabView />;
    }

    // --- LOADING ---
    // En la landing no se esperan los ajustes: con sesión, WorkspaceContext deja
    // el taller sin resolver ahí a propósito (nada de capa de datos en la
    // página de marketing), y sin taller los ajustes no se cargan nunca — este
    // loader se quedaría girando para siempre. La landing se pinta más abajo,
    // antes de cualquier puerta que necesite ajustes.
    if (authLoading || (isAuthorized && settingsLoading && !esLanding(location.pathname))) {
        /* Mismo texto, mismos puntos suspensivos que `index.html`: este loader
           releva a aquel, y cualquier diferencia se ve como un parpadeo. */
        return <RazaLoader fullScreen size="lg" message="Cargando Raza Pro…" />;
    }

    /* --- SESIÓN DESCONOCIDA ---
       Sin sesión en el estado de React, pero tampoco una respuesta firme de que
       no la haya: el arranque de auth se acabó por el temporizador de
       seguridad. Es el caso de la fila `6c2d43ef` del registro, que llevaba un
       `user_email` y un `workspace_id` sacados del JWT POR EL SERVIDOR — la
       sesión estaba viva mientras React la tenía en `null`.

       Aquí abajo hay un `<Navigate to="/" replace />`. Aplicado a este caso
       significa: a alguien con sesión válida leyendo `/proposal/<id>` lo
       mandaba a la página de marketing, y el `replace` le borraba del historial
       la ruta a la que quería volver. Se queda donde está y se le ofrece
       reintentar. */
    if (!currentUser && !authLoading && !isPublicRoute && authStalled) {
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-acero-50 p-4">
                <div className="bg-white rounded-2xl shadow-s2 p-8 max-w-md w-full text-center border border-acero-200">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-aviso-bg flex items-center justify-center">
                        <RazaLoader size="sm" />
                    </div>
                    <h2 className="text-lg font-semibold tracking-tight text-navy-950 mb-2">{t('app.error.titulo')}</h2>
                    <p className="text-acero-500 text-sm mb-6">{t('comun.sinConexionSeguro')}</p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => retryAuth()}
                            className="bg-brand-400 text-navy-950 px-6 py-2 rounded-lg text-sm font-bold hover:bg-brand-300 transition-colors"
                        >
                            {t('app.acceso.reintentar')}
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            className="presionable bg-navy-850 text-white h-10 px-5 rounded-xl text-sm font-semibold hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                        >
                            {t('app.error.recargar')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // If not logged in and trying to access a protected route, redirect to landing
    if (!currentUser && !authLoading && !isPublicRoute) {
        return <Navigate to="/" replace />;
    }

    // Landing page route — always accessible (marketing/SEO page)
    if (location.pathname === '/' && !authLoading) {
        return <LandingPage />;
    }

    // --- SUPER ADMIN ISOLATION ---
    // Super admin gets a completely separate UI — no standard app, no navigation bar
    if (isSuperAdmin && currentUser) {
        return (
            <div className="min-h-dvh bg-acero-50">
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        <Route path="*" element={<SuperAdminView />} />
                    </Routes>
                </Suspense>
            </div>
        );
    }

    // --- UNAUTHORIZED (logged in but not in allowed_users) ---
    if (currentUser && !isAuthorized && !authLoading && !isPublicRoute) {
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-acero-50 p-4">
                <div className="bg-white rounded-2xl shadow-s2 p-8 max-w-md w-full text-center border border-acero-200">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-peligro-bg flex items-center justify-center">
                        <ShieldX className="text-peligro" size={22} />
                    </div>
                    <h2 className="text-lg font-semibold tracking-tight text-navy-950 mb-2">{t('app.acceso.denegado')}</h2>
                    <p className="text-acero-600 text-sm mb-1">{t('app.sesion.como')} <strong>{currentUser.email}</strong></p>
                    <p className="text-acero-500 text-sm mb-6">{t('app.acceso.desc')}</p>
                    {authError && <p className="text-peligro text-xs mb-4">{authError}</p>}
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={retryAuth}
                            className="bg-brand-400 text-navy-950 px-6 py-2 rounded-lg text-sm font-bold hover:bg-brand-300 transition-colors"
                        >
                            {t('app.acceso.reintentar')}
                        </button>
                        <button
                            onClick={logout}
                            className="presionable bg-navy-850 text-white h-10 px-5 rounded-xl text-sm font-semibold hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                        >
{t('app.acceso.salir')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- CLIENT PORTAL ---
    if (clientId) {
        return <ClientPortalLayout />;
    }

    // --- WORKSPACE UNRESOLVED ---
    // The workspace fetch failed or returned empty (storage eviction, RLS, or a
    // network failure). Never fall through to the CRM/wizard on the 'default'
    // fallback — that's what made a user's own data look "deleted". Offer a
    // retry; their data is intact, just not yet scoped to.
    if (isAuthorized && workspaceStatus === 'error') {
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-acero-50 p-4">
                <div className="bg-white rounded-2xl shadow-s2 p-8 max-w-md w-full text-center border border-acero-200">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-aviso-bg flex items-center justify-center">
                        <RazaLoader size="sm" />
                    </div>
                    <h2 className="text-lg font-semibold tracking-tight text-navy-950 mb-2">{t('app.workspace.error_titulo')}</h2>
                    <p className="text-acero-500 text-sm mb-6">{t('comun.sinConexionSeguro')}</p>
                    <button
                        onClick={() => retryWorkspace()}
                        className="presionable bg-navy-850 text-white h-10 px-5 rounded-xl text-sm font-semibold hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // --- SETTINGS LOAD FAILED ---
    // Never fall through to the Setup Wizard on a failed load: with defaults in
    // state (isSetupComplete=false) the wizard's auto-save would overwrite the
    // tenant's real settings. Show a retry instead; their data is untouched.
    if (isAuthorized && loadFailed) {
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-acero-50 p-4">
                <div className="bg-white rounded-2xl shadow-s2 p-8 max-w-md w-full text-center border border-acero-200">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-aviso-bg flex items-center justify-center">
                        <RazaLoader size="sm" />
                    </div>
                    <h2 className="text-lg font-semibold tracking-tight text-navy-950 mb-2">{t('app.settings.error_titulo')}</h2>
                    <p className="text-acero-500 text-sm mb-6">{t('comun.sinConexionSeguro')}</p>
                    <button
                        onClick={() => retryLoad()}
                        className="presionable bg-navy-850 text-white h-10 px-5 rounded-xl text-sm font-semibold hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // --- INITIAL SETUP (first-time tenant) ---
    if (isAuthorized && !loadFailed && !settings.isSetupComplete) {
        if (userRole === 'Admin') {
            return (
                <div className="min-h-dvh bg-acero-50 flex items-center justify-center p-4">
                    <Suspense fallback={<LoadingFallback />}>
                        <SetupWizard isInitialSetup={true} />
                    </Suspense>
                </div>
            );
        }
        return (
            <div className="h-dvh w-full flex items-center justify-center bg-acero-50 p-4">
                <div className="bg-white rounded-2xl shadow-s2 p-8 max-w-md w-full text-center border border-acero-200">
                    <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-aviso-bg flex items-center justify-center">
                        <RazaLoader size="sm" />
                    </div>
                    <h2 className="text-lg font-semibold tracking-tight text-navy-950 mb-2">{t('app.setup.titulo')}</h2>
                    <p className="text-acero-600 text-sm mb-1">{t('app.sesion.como')} <strong>{currentUser?.email}</strong></p>
                    <p className="text-acero-500 text-sm mb-6">{t('app.setup.desc')}</p>
                    <button
                        onClick={logout}
                        className="presionable bg-navy-850 text-white h-10 px-5 rounded-xl text-sm font-semibold hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                    >{t('app.setup.salir')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <WorkflowController>
            {/* `overflow-x-clip`, NO `overflow-x-hidden` (en el div de abajo).
                `hidden` en un eje obliga al otro a calcularse como `auto`
                (CSS Overflow 3 §3.1), así que ese div era un contenedor de
                scroll. Y como `min-h-dvh` es un mínimo y no una altura, su
                rango de scroll siempre era 0: no se movía nunca, pero era el
                scrollport más cercano de TODOS los `sticky` de la app — y un
                `sticky` anclado a algo que no scrollea se comporta como
                `static`. Por eso nada se quedaba fijo en ninguna pantalla: la
                barra de arriba del móvil, los encabezados de tabla, la cabecera
                de Estimados. Ninguno llegó a funcionar nunca.
                `clip` recorta igual en horizontal pero NO crea contenedor de
                scroll, así que `overflow-y` se queda en `visible` y los
                `sticky` se anclan al viewport, que es lo que se quería. */}
            {(workflow) => (
                <div className="flex min-h-dvh bg-acero-100 font-sans text-navy-950 selection:bg-brand-200 selection:text-navy-950 overflow-x-clip">
                    {/* Sidebar — always visible on lg, toggleable on mobile */}
                    {currentView !== 'ProposalViewer' && currentView !== 'DesignShowcase' && currentView !== 'AIAssistant' && (
                        <Sidebar
                            currentView={currentView}
                            onViewChange={(view: any) => navigateTo(view)}
                            isOpen={isMobileSidebarOpen}
                            onClose={() => setIsMobileSidebarOpen(false)}
                            collapsed={isSidebarCollapsed}
                            onToggleCollapse={toggleSidebarCollapsed}
                        />
                    )}

                    {/* Main Content Area */}
                    <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-[margin] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${currentView !== 'ProposalViewer' && currentView !== 'DesignShowcase' && currentView !== 'AIAssistant' ? (isSidebarCollapsed ? 'lg:ml-[68px]' : 'lg:ml-[220px]') : ''}`}>

                        {/* Mobile Top Bar — only shows on small screens */}
                        {currentView !== 'ProposalViewer' && currentView !== 'DesignShowcase' && currentView !== 'AIAssistant' && (
                            /* Blanco opaco, sin `backdrop-blur-xl`.
                               Esta barra es `lg:hidden`: existe SOLO en el
                               teléfono, así que el desenfoque no era un lujo de
                               escritorio que además se veía en móvil — era un
                               costo exclusivamente móvil.
                               Y es el peor sitio para ponerlo: una barra
                               `sticky` obliga a Safari a rehacer el desenfoque
                               de todo lo que pasa por debajo en CADA fotograma
                               del scroll, que es justo cuando el hilo ya está
                               ocupado. Es la causa clásica de que una lista
                               larga «arrastre» en iPhone.
                               Lo que se pierde es casi nada: era blanco al 90%
                               sobre un fondo acero-100 (#ECEFF5), o sea un 10%
                               de un gris muy claro colándose. En una captura
                               lado a lado la diferencia no se distingue. Si
                               algún día el fondo de la app se vuelve oscuro o
                               con imagen, esto sí se notaría y habría que
                               volver a mirarlo. */
                            <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-acero-200 px-3 h-14 flex items-center justify-between">
                                <button
                                    onClick={() => setIsMobileSidebarOpen(true)}
                                    aria-label={t('aria.abrirMenu')}
                                    className="presionable p-2 -ml-1 text-navy-950 hover:bg-acero-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                                </button>
                                {/* El nombre de la pantalla, traducido. Aquí se imprimía
                                    `currentView`, que es el identificador interno: en el
                                    teléfono se leía «FollowUps» y «Pricebook» con toda la
                                    app en español (ver i18n/titulosDeVista.ts). */}
                                <span className="font-semibold tracking-tight text-navy-950 text-[15px]">{tituloDeVista(currentView, t)}</span>
                                <div className="flex items-center gap-1">
                                    <NotificationsBell />
                                    <div className="w-8 h-8 rounded-lg bg-acero-100 ring-1 ring-acero-200 overflow-hidden">
                                        {currentUser?.photoURL ? (
                                            <img src={currentUser.photoURL} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-navy-950 text-xs font-semibold bg-brand-400">
                                                {(currentUser?.displayName || 'U').charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Page Content */}
                        <div className={`flex-1 w-full ${currentView !== 'ProposalViewer' && currentView !== 'DesignShowcase' && currentView !== 'AIAssistant' ? 'px-4 sm:px-6 md:px-8 py-6 pb-10' : ''}`}>
                            {/* Centrado y POR DEBAJO de la barra superior del teléfono (h-14).
                                Iba a `top-2 right-4`, o sea justo encima de la
                                campana y del avatar de esa misma barra.
                                `pointer-events-none` en el envoltorio para no
                                tapar los clics de lo que hay detrás; el aviso se
                                los devuelve solo a sí mismo. */}
                            <div className="fixed top-[4rem] lg:top-3 left-1/2 -translate-x-1/2 z-[100] pointer-events-none max-w-[calc(100vw-2rem)]">
                                <OfflineBadge />
                            </div>
                            {/* Ancho máximo centralizado (Sistema 2.0): el contenido
                                nunca se estira más de 1180px en pantallas anchas. */}
                            <div className={currentView !== 'ProposalViewer' && currentView !== 'DesignShowcase' && currentView !== 'AIAssistant' ? 'max-w-[1180px] mx-auto w-full' : 'contents'}>
                            <Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                    <Route path="/dashboard" element={<DashboardView onQuickAction={workflow.triggerQuickAction} />} />
                                    <Route path="/leads" element={<Navigate to="/clients?tab=leads" replace />} />
                                    <Route path="/clients" element={
                                        <ClientsView
                                            onCreateEstimate={(id) => workflow.startJobFlow(id, { view: 'Clients', clientId: id })}
                                            onOpenEstimateEditor={(est) => workflow.openEstimateEditor(est, { view: 'Clients', clientId: est.clientId })}
                                            onCreateProposalForLead={(leadId) => workflow.startProposalForLead(leadId)}
                                        />
                                    } />
                                    <Route path="/estimates" element={
                                        <EstimatesView
                                            onEdit={(est) => workflow.openEstimateEditor(est, { view: 'Estimates', clientId: est.clientId })}
                                            onCreateEstimate={() => workflow.startJobFlow(undefined, { view: 'Estimates' })}
                                        />
                                    } />
                                    <Route path="/projects" element={<ProjectsView />} />
                                    <Route path="/projects/:id" element={<ProjectsView />} />
                                    <Route path="/finance" element={<FinanceView />} />

                                    <Route path="/follow-ups" element={<FollowUpsView />} />

                                    <Route path="/schedule" element={<CalendarView />} />
                                    <Route path="/materials" element={<PricebookView />} />
                                    <Route path="/templates" element={<Navigate to="/materials?section=templates" replace />} />
                                    <Route path="/services" element={<ServicesView />} />
                                    <Route path="/timeclock" element={<WorkforceView initialTab="clockin" />} />
                                    <Route path="/workforce" element={<WorkforceView />} />
                                    <Route path="/inventory" element={<InventoryView />} />
                                    {/* Deliberately ungated: training is for every role, including
                                        the clock-only crew. NavigationContext.ALWAYS_ALLOWED_VIEWS
                                        is the matching half of this decision. */}
                                    <Route path="/classes" element={<ClassesView />} />

                                    <Route path="/settings" element={<SettingsView />} />
                                    {/* P-1: id-bearing route so the editor survives refresh/deep-link.
                                        Bare /proposal kept for any legacy navigation (NavigationContext
                                        restores activeProposalId from the :id segment). */}
                                    <Route path="/proposal/:id" element={<ProposalView />} />
                                    <Route path="/proposal" element={<ProposalView />} />
                                    <Route path="/public/proposal/:id" element={<PublicProposalRoute />} />
                                    <Route path="/admin" element={<SuperAdminView />} />
                                    {/* Typing the URL must not bypass the role check. */}
                                    <Route
                                        path="/ai-assistant"
                                        element={canUseAI(userRole) ? <AIChatView /> : <Navigate to="/dashboard" replace />}
                                    />
                                    {/* Dirección desconocida: se DICE, no se disimula.
                                        Esto mandaba al panel en silencio, así
                                        que un enlace roto se leía como «la app
                                        me sacó de donde estaba» y nadie
                                        reportaba nunca los enlaces rotos. */}
                                    <Route path="*" element={<NotFoundView />} />
                                </Routes>
                            </Suspense>
                            </div>
                        </div>

                </div>

                    {/* AI Chat Panel — floating button + drawer. Not mounted for
                        field roles: the assistant is handed full financial context,
                        so leaving it reachable would undo the money restrictions. */}
                    {canUseAI(userRole) && <AIChatPanel />}

                    {/* Always-listening voice conversation modal (mounts at app root) */}
                    <VoiceConversationModal />

                    {/* Movable tutorial video box — mounts at app root so it
                        persists across navigation and never disappears while the
                        user keeps working. */}
                    <HelpVideoBox />

                </div>
            )}
        </WorkflowController>
    );
};

// Public Proposal route wrapper
import { useParams } from 'react-router-dom';
const PublicProposalRoute = () => {
    const { t } = useIdioma();
    const { id } = useParams();
    if (!id) return <div>{t('app.propuesta.link_invalido')}</div>;
    return <PublicProposalView proposalId={id} />;
};

/* Respaldo global de las LECTURAS. Se instala al cargar el módulo, antes de que
   ningún `useQuery` llegue a correr: hasta ahora sólo las escrituras tenían red
   de seguridad (`MutationCache.onError`), y una lectura podía fallar sin dejar
   rastro en ninguna parte. Ver `services/vigilarLecturas.ts`. */
vigilarLecturas(queryClient);

const App: React.FC = () => {
    return (
        /* Sin `<ErrorBoundary>` aquí a propósito: `index.tsx` ya envuelve
           `<App/>` entero con el de `components/`, así que uno aquí dentro solo
           serviría para cazar antes que él. Ver el comentario de arriba. */
            <Router>
                <AuthProvider>
                    <WorkspaceProvider>
                        <GoogleMapsProvider>
                            <QueryClientProvider client={queryClient}>
                                <SettingsProvider>
                                    <DataProviders>
                                        <NavigationProvider>
                                            <AIChatProvider>
                                            <DialogProvider>
                                            <NotificationsProvider>
                                            <HelpVideoProvider>
                                                <AppContent />
                                                {/* Toasts — Sistema 2.0: navy de marca, sombra entintada,
                                                    verde/rojo semánticos. Abajo a la derecha: no tapa el
                                                    encabezado de la vista ni la acción primaria. */}
                                                <Toaster
                                                    position="bottom-right"
                                                    gutter={10}
                                                    toastOptions={{
                                                        duration: 3200,
                                                        style: {
                                                            background: '#00346F',
                                                            color: '#FFFFFF',
                                                            borderRadius: '12px',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            padding: '11px 16px',
                                                            maxWidth: '380px',
                                                            boxShadow: '0 12px 32px -8px rgba(11, 22, 41, 0.28)',
                                                        },
                                                        success: {
                                                            iconTheme: { primary: '#188A4C', secondary: '#FFFFFF' },
                                                        },
                                                        error: {
                                                            iconTheme: { primary: '#D92D20', secondary: '#FFFFFF' },
                                                            duration: 4500,
                                                        },
                                                        loading: {
                                                            iconTheme: { primary: '#FACC15', secondary: '#0B1629' },
                                                        },
                                                    }}
                                                />
                                            </HelpVideoProvider>
                                            </NotificationsProvider>
                                            </DialogProvider>
                                            </AIChatProvider>
                                        </NavigationProvider>
                                    </DataProviders>
                                </SettingsProvider>
                            </QueryClientProvider>
                        </GoogleMapsProvider>
                    </WorkspaceProvider>
                </AuthProvider>
            </Router>
    );
};

export default App;
